// One invoice: lines, manual additions, finalise, PDF, payments (doc 03 §4.2).
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { api, type Invoice, type Me } from "../api";
import {
  Badge, Card, ConfirmButton, Empty, ErrorNote, Field, Modal, PageHeader, Spinner,
  formatDate, formatMoney, formatTime, useAction,
} from "../ui";

export default function InvoiceDetail({ me }: { me: Me }) {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [addingLine, setAddingLine] = useState(false);
  const [recording, setRecording] = useState(false);
  const { run } = useAction();

  const { data, isLoading, error } = useQuery<Invoice>({
    queryKey: ["invoice", id],
    queryFn: () => api.get(`/v1/invoices/${id}`),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["invoice", id] });
  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const isAdmin = me.role === "nci_admin";
  const editable = data.state === "draft" && isAdmin;

  return (
    <>
      <PageHeader
        title={data.number ?? `Draft invoice #${data.id}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-4">
            <span>{data.reseller_name}</span>
            <span>{formatDate(data.period_start)} – {formatDate(data.period_end)}</span>
            <Badge tone={data.state === "final" ? "ok" : data.state === "void" ? "idle" : "info"}>{data.state}</Badge>
          </span>
        }
        actions={
          <>
            {data.state === "final" && (
              <a className="btn-secondary" href={`/v1/invoices/${data.id}/pdf`} target="_blank" rel="noreferrer">
                Download PDF
              </a>
            )}
            {editable && <button className="btn-secondary" onClick={() => setAddingLine(true)}>Add line</button>}
            {editable && (
              <ConfirmButton
                label="Finalise"
                confirmLabel="Finalise — click again to send it"
                onConfirm={() => run(async () => { await api.post(`/v1/invoices/${data.id}/finalize`); refresh(); }, "Invoice finalised and emailed")}
              />
            )}
            {isAdmin && data.state === "final" && (
              <ConfirmButton danger label="Void" onConfirm={() => run(async () => { await api.post(`/v1/invoices/${data.id}/void`); refresh(); }, "Invoice voided")} />
            )}
            {isAdmin && data.state === "final" && (
              <button className="btn-primary" onClick={() => setRecording(true)}>Record payment</button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Card title="Lines" padded={false}>
          {!data.lines.length ? <Empty>No lines.</Empty> : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr><th>Description</th><th>Outpost</th><th>Qty</th><th>Unit</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {data.lines.map((line) => (
                    <tr key={line.id}>
                      <td>
                        {line.description}
                        {line.manual && <Badge tone="info" className="ml-2">manual</Badge>}
                        {line.detail_json?.prorated ? (
                          <div className="mt-0.5 text-xs text-ink-400">
                            prorated {String(line.detail_json.days_active)} of {String(line.detail_json.days_in_period)} days
                          </div>
                        ) : null}
                        {line.detail_json?.unpriced ? (
                          <div className="mt-0.5 text-xs text-state-warn">no rate assigned — billed at zero, not omitted</div>
                        ) : null}
                      </td>
                      <td className="font-mono text-xs text-ink-400">{line.endpoint_id ?? "—"}</td>
                      <td className="tabular-nums">{parseFloat(line.qty).toFixed(2)}</td>
                      <td className="tabular-nums">{formatMoney(line.unit_price, data.currency)}</td>
                      <td className="tabular-nums font-medium">{formatMoney(line.amount, data.currency)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-ink-500">
                    <td colSpan={4} className="px-3 py-2 text-right font-medium">Total</td>
                    <td className="px-3 py-2 tabular-nums text-lg font-semibold">{formatMoney(data.total, data.currency)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card title="Summary">
            <dl className="space-y-2 text-sm">
              <Row label="Total" value={formatMoney(data.total, data.currency)} />
              <Row label="Paid" value={formatMoney(data.paid ?? 0, data.currency)} />
              <Row label="Balance" value={formatMoney(data.balance ?? data.total, data.currency)} />
              <Row label="Due" value={formatDate(data.due_date)} />
              <Row label="Finalised" value={data.finalized_at ? `${formatTime(data.finalized_at)}` : "—"} />
              <Row label="Emailed" value={data.emailed_at ? formatTime(data.emailed_at) : "not sent"} />
            </dl>
          </Card>

          <Card title="Payments" padded={false}>
            {!data.payments.length ? <Empty>No payments recorded.</Empty> : (
              <ul className="divide-y divide-ink-700 text-sm">
                {data.payments.map((payment) => (
                  <li key={payment.id} className="px-4 py-2">
                    <div className="flex justify-between">
                      <span className="tabular-nums font-medium">{formatMoney(payment.amount, data.currency)}</span>
                      <span className="text-ink-400">{formatDate(payment.at)}</span>
                    </div>
                    <div className="text-xs text-ink-400">
                      {payment.method_note ?? "—"}{payment.reference ? ` · ${payment.reference}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {addingLine && <LineModal invoiceId={data.id} onClose={() => setAddingLine(false)} onDone={() => { setAddingLine(false); refresh(); }} />}
      {recording && <PaymentModal invoiceId={data.id} balance={data.balance ?? data.total} onClose={() => setRecording(false)} onDone={() => { setRecording(false); refresh(); }} />}
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between">
      <dt className="text-ink-400">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

function LineModal({ invoiceId, onClose, onDone }: { invoiceId: number; onClose: () => void; onDone: () => void }) {
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");
  const { run, busy } = useAction();

  return (
    <Modal
      title="Add a manual line"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || !description || !unit}
            onClick={() => run(async () => {
              await api.post(`/v1/invoices/${invoiceId}/lines`, { description, qty, unit_price: unit });
              onDone();
            }, "Line added")}
          >{busy ? "Adding…" : "Add"}</button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Description" hint="Shipping, a truck roll, a credit — anything the meter cannot know.">
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Quantity"><input className="input" type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
          <Field label="Unit price" hint="Negative for a credit."><input className="input" type="number" step="0.01" value={unit} onChange={(e) => setUnit(e.target.value)} /></Field>
        </div>
      </div>
    </Modal>
  );
}

function PaymentModal({ invoiceId, balance, onClose, onDone }: {
  invoiceId: number; balance: string; onClose: () => void; onDone: () => void;
}) {
  const [amount, setAmount] = useState(balance);
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const { run, busy } = useAction();

  return (
    <Modal
      title="Record a payment"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy || !amount}
            onClick={() => run(async () => {
              await api.post(`/v1/invoices/${invoiceId}/payments`, { amount, method_note: method || null, reference: reference || null });
              onDone();
            }, "Payment recorded")}
          >{busy ? "Recording…" : "Record"}</button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Amount" hint="Partial payments are fine; the balance updates."><input className="input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Method"><input className="input" placeholder="ACH, cheque, wire…" value={method} onChange={(e) => setMethod(e.target.value)} /></Field>
        <Field label="Reference"><input className="input" value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
